import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Users, CheckCircle2, XCircle, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      className="border border-zinc-200 bg-white p-5 flex items-start gap-4"
      data-testid={testid}
    >
      <div
        className="h-11 w-11 rounded-sm flex items-center justify-center text-white shrink-0"
        style={{ backgroundColor: accent || "#09090B" }}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {label}
        </div>
        <div className="mt-1 text-3xl font-black tracking-tighter tabular-nums font-mono">
          {value}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        console.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        subtitle="Operasyon panosu"
        title="Yönetici Paneli"
      />
      <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-sm" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Stat
                label="Bugün oluşan taslak"
                value={stats?.drafts_today ?? 0}
                icon={FileText}
                accent="#002FA7"
                testid="stat-drafts-today"
              />
              <Stat
                label="Bu hafta"
                value={stats?.drafts_week ?? 0}
                icon={CheckCircle2}
                accent="#00875A"
                testid="stat-drafts-week"
              />
              <Stat
                label="Bugün hata"
                value={stats?.failed_today ?? 0}
                icon={XCircle}
                accent="#B42318"
                testid="stat-failed-today"
              />
              <Stat
                label="Aktif kullanıcı"
                value={`${stats?.active_users ?? 0} / ${stats?.total_users ?? 0}`}
                icon={Users}
                accent="#09090B"
                testid="stat-users"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 border border-zinc-200 bg-white">
                <div className="border-b border-zinc-200 px-4 py-3 flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-[#002FA7]" />
                  <div className="text-sm font-bold">Kullanıcı bazında taslak üretimi</div>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                        Operatör
                      </th>
                      <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                        Taslak
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.per_user || []).length === 0 ? (
                      <tr>
                        <td colSpan={2} className="p-8 text-center text-sm text-zinc-500">
                          Henüz kayıt yok
                        </td>
                      </tr>
                    ) : (
                      stats.per_user.map((u) => (
                        <tr key={u.user_id} className="border-b border-zinc-200" data-testid={`per-user-${u.user_id}`}>
                          <td className="px-3 py-3">
                            <div className="text-sm font-medium">{u.name || "—"}</div>
                            <div className="font-mono text-[11px] text-zinc-500">{u.email}</div>
                          </td>
                          <td className="px-3 py-3 text-right font-mono tabular-nums text-lg font-black">
                            {u.count}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="border border-zinc-200 bg-zinc-950 text-white p-5 flex flex-col">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Toplam
                </div>
                <div className="mt-2 text-5xl font-black tracking-tighter font-mono tabular-nums">
                  {stats?.total_drafts ?? 0}
                </div>
                <div className="mt-1 text-sm text-zinc-400">başarılı taslak sipariş</div>

                <div className="mt-auto pt-6 space-y-2">
                  <Link
                    to="/carts"
                    className="block w-full text-center border border-white/20 hover:border-white text-white text-xs uppercase tracking-widest font-mono py-2 rounded-sm transition-colors duration-150"
                    data-testid="dashboard-goto-carts"
                  >
                    Aktif sepetlere git →
                  </Link>
                  <Link
                    to="/history"
                    className="block w-full text-center border border-white/10 hover:border-white/40 text-zinc-400 hover:text-white text-xs uppercase tracking-widest font-mono py-2 rounded-sm transition-colors duration-150"
                    data-testid="dashboard-goto-history"
                  >
                    Geçmişi incele
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
